import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAllUpcomingEvents, useAllChecklistProgress, CATEGORIES, formatCategory } from "@/hooks/useChecklistsData";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const pctColor = (pct: number) => {
  if (pct >= 75) return "text-green-700 bg-green-100";
  if (pct >= 50) return "text-yellow-700 bg-yellow-100";
  if (pct >= 25) return "text-orange-700 bg-orange-100";
  return "text-red-700 bg-red-100";
};

const OverviewDashboardTab: React.FC<{ onSelectEvent?: (eventId: string) => void }> = ({ onSelectEvent }) => {
  const { data: events = [], isLoading: eventsLoading } = useAllUpcomingEvents();
  const { data: allProgress = [], isLoading: progressLoading } = useAllChecklistProgress();

  const isLoading = eventsLoading || progressLoading;

  const getEventProgress = (eventId: string) => {
    const eventProg = allProgress.filter((p: any) => p.event_id === eventId);
    const byCat: Record<string, number> = {};
    let totalTasks = 0, totalDone = 0;

    CATEGORIES.forEach(cat => {
      const p = eventProg.find((ep: any) => ep.category === cat);
      byCat[cat] = p?.pct_complete ?? -1; // -1 means no tasks
      totalTasks += p?.total_tasks ?? 0;
      totalDone += p?.completed ?? 0;
    });

    const overall = totalTasks > 0 ? Math.round((totalDone / totalTasks) * 100) : -1;
    return { byCat, overall, totalTasks };
  };

  const displayCats = ["signage", "staffing", "travel", "officials", "scorekeepers", "venue_prep"] as const;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Readiness overview across all upcoming events.</p>

      {isLoading ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">Loading...</CardContent></Card>
      ) : events.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No upcoming events.</CardContent></Card>
      ) : (
        <div className="rounded-md border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Dates</TableHead>
                {displayCats.map(cat => (
                  <TableHead key={cat} className="text-center text-xs">{formatCategory(cat)}</TableHead>
                ))}
                <TableHead className="text-center">Overall</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event: any) => {
                const prog = getEventProgress(event.id);
                return (
                  <TableRow
                    key={event.id}
                    className="cursor-pointer hover:bg-muted/80"
                    onClick={() => onSelectEvent?.(event.id)}
                  >
                    <TableCell className="font-medium">{event.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(parseLocalDate(event.start_date), "MMM d")} – {format(parseLocalDate(event.end_date), "MMM d")}
                    </TableCell>
                    {displayCats.map(cat => (
                      <TableCell key={cat} className="text-center">
                        {prog.byCat[cat] === -1 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <Badge variant="outline" className={`text-xs ${pctColor(prog.byCat[cat])}`}>
                            {prog.byCat[cat]}%
                          </Badge>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      {prog.overall === -1 ? (
                        <span className="text-xs text-muted-foreground">No tasks</span>
                      ) : (
                        <Badge className={`text-xs ${pctColor(prog.overall)}`}>
                          {prog.overall}%
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default OverviewDashboardTab;
